import React, { useState, useEffect } from "react";
import { Copy, Check, Linkedin, Twitter, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

/**
 * Reusable share dialog for deals / memos / playbooks.
 *  - Appends UTM params to the share URL per channel
 *  - Renders LinkedIn post templates with per-template copy buttons
 *  - Tracks every click (linkedin / twitter / copy / template_copy)
 */
export const ShareDialog = ({ open, onOpenChange, type, entityId, entityTitle, shareText }) => {
  const [copied, setCopied] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [copiedIdx, setCopiedIdx] = useState(null);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const base = `${origin}/api/share/${type}/${entityId}`;

  const urlWithUtm = (source) => {
    const params = new URLSearchParams({
      utm_source: source,
      utm_medium: "social",
      utm_campaign: entityId,
    });
    return `${base}?${params.toString()}`;
  };

  const plainShareUrl = urlWithUtm("copy");
  const ogImage = `${API}/og/${type}/${entityId}.png`;

  // Fetch templates when opened
  useEffect(() => {
    if (!open) return;
    api
      .get(`/share/templates/${type}/${entityId}`)
      .then((r) => setTemplates(r.data.templates || []))
      .catch(() => setTemplates([]));
  }, [open, type, entityId]);

  const track = (channel) => {
    api
      .post("/share/track", { type, entity_id: entityId, channel })
      .catch(() => {});
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(plainShareUrl);
      setCopied(true);
      track("copy");
      toast.success("Share link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed");
    }
  };

  const openLinkedIn = () => {
    track("linkedin");
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(urlWithUtm("linkedin"))}`;
    window.open(url, "_blank", "noopener,width=720,height=640");
  };

  const openTwitter = () => {
    track("twitter");
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(urlWithUtm("twitter"))}`;
    window.open(url, "_blank", "noopener,width=720,height=640");
  };

  const copyTemplate = async (idx, body) => {
    const postText = `${body}\n\n${urlWithUtm("linkedin")}`;
    try {
      await navigator.clipboard.writeText(postText);
      setCopiedIdx(idx);
      track("copy");
      toast.success("Post copied", { description: "Paste straight into LinkedIn." });
      setTimeout(() => setCopiedIdx(null), 2200);
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid={`share-dialog-${type}`}>
        <DialogHeader>
          <DialogTitle>Share this {type}</DialogTitle>
          <DialogDescription>
            Branded preview auto-generated for{" "}
            <span className="text-foreground">{entityTitle}</span>. Every click is tracked, every signup attributed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* OG preview */}
          <div className="card-institutional overflow-hidden" data-testid="og-preview-card">
            <img
              src={ogImage}
              alt={`Preview card for ${entityTitle}`}
              className="w-full block"
              style={{ aspectRatio: "1200/630" }}
            />
          </div>

          {/* Shareable link */}
          <div>
            <label className="overline block mb-2">Shareable link</label>
            <div className="flex items-center gap-2 border border-border rounded-md px-3 h-11">
              <input
                readOnly
                value={plainShareUrl}
                className="flex-1 bg-transparent outline-none text-xs font-mono truncate"
                data-testid="share-url-input"
                onFocus={(e) => e.target.select()}
              />
              <button
                onClick={copyLink}
                className="text-xs inline-flex items-center gap-1.5 gold-text hover:text-foreground transition-colors"
                data-testid="share-copy-button"
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </div>

          {/* Direct share buttons */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={openLinkedIn}
              className="btn-ghost"
              data-testid="share-linkedin-button"
            >
              <Linkedin className="h-4 w-4" /> Share on LinkedIn
            </button>
            <button
              onClick={openTwitter}
              className="btn-ghost"
              data-testid="share-twitter-button"
            >
              <Twitter className="h-4 w-4" /> Share on X
            </button>
          </div>

          {/* LinkedIn post templates */}
          {templates.length > 0 && (
            <div className="pt-2" data-testid="share-templates">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="h-4 w-4 gold-text" strokeWidth={1.5} />
                <span className="overline gold-text">Ready-to-post hooks</span>
              </div>
              <div className="space-y-2.5">
                {templates.map((t, i) => (
                  <div
                    key={i}
                    className="border border-border rounded-md p-4 hover:border-[hsl(var(--gold))] transition-colors group"
                    data-testid={`share-template-${i}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                        {t.angle}
                      </span>
                      <button
                        onClick={() => copyTemplate(i, t.body)}
                        className="text-xs inline-flex items-center gap-1.5 gold-text hover:text-foreground"
                        data-testid={`share-template-copy-${i}`}
                      >
                        {copiedIdx === i ? (
                          <>
                            <Check className="h-3 w-3" /> Copied
                          </>
                        ) : (
                          <>
                            <Copy className="h-3 w-3" /> Copy post
                          </>
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-foreground/80 whitespace-pre-wrap leading-relaxed">
                      {t.body}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
